import { api, isApiError } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'

import { uploadResultDtoSchema, type UploadResultDto } from '@lib/dto/upload.dto'

/** Thrown when object storage isn't configured server-side (503). */
export class UploadsUnavailableError extends Error {}

/**
 * Uploads a resized image (a base64 data URL) to private object storage via the
 * API. Returns `{ key, url }` — persist `key` on the entity; use `url` only for
 * immediate preview.
 */
export const uploadService = {
  async uploadImage(dataUrl: string): Promise<UploadResultDto> {
    try {
      return await api.post('/uploads', { data: dataUrl }, uploadResultDtoSchema)
    } catch (error) {
      if (isApiError(error) && error.status === 503) {
        throw new UploadsUnavailableError('Image uploads are not configured.')
      }
      logRequestFailure('Image upload failed', {}, error)
      throw error
    }
  },

  /**
   * What to save on the record for a resized image: its storage key, or the data
   * URL itself where object storage is off — the third form every image field
   * accepts. One place, so a cover and a room photograph cannot disagree about
   * whether a photograph can be added without storage.
   */
  async storeImage(dataUrl: string): Promise<string> {
    try {
      const { key } = await uploadService.uploadImage(dataUrl)
      return key
    } catch (error) {
      if (error instanceof UploadsUnavailableError) return dataUrl
      throw error
    }
  },
}
